import React from 'react';
import { Button, Tag, Typography } from 'antd';
import type { WorkspaceApplication, WorkspaceTask } from '../../store/api/workspaceApi';
import { buildInboxTaskRowPresentation } from '../../domain/attentionInboxRowPresentation';
import { isRouteGroupDocumentWorkflowEnabled } from '../../features/routeGroups/routeGroupFeatureFlags';
import { canOpenWorkspaceTaskModal } from './workspaceTaskModalResolve';
import type { AttentionPriority, AttentionSignal } from '../../domain/workspaceAttentionInbox';

const { Text } = Typography;

export const PRIORITY_COLOR: Record<AttentionPriority, string> = {
  P0: '#cf1322',
  P1: '#d46b08',
  P2: '#d4b106',
  P3: '#8c8c8c',
};

export function priorityDueLine(signal: AttentionSignal): string | null {
  if (signal.overdueDays != null && signal.overdueDays > 0) {
    const n = signal.overdueDays;
    const word = n === 1 ? 'день' : n < 5 ? 'дня' : 'дней';
    return `Просрочено ${n} ${word}`;
  }
  if (signal.dueDate) {
    return `Срок ${signal.dueDate}`;
  }
  return null;
}

export function PriorityBadge({ priority }: { priority: AttentionPriority }) {
  return (
    <Tag
      color={PRIORITY_COLOR[priority]}
      style={{ margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 4px' }}
    >
      {priority}
    </Tag>
  );
}

export function ClosureBlockedBadge() {
  return (
    <Tag color="blue" style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>
      Закрытие заблокировано
    </Tag>
  );
}

export function AttentionInboxSignalRow({
  signal,
  closureBlocked,
  applications,
  onOpenFinanceContext,
  onTaskAction,
}: {
  signal: AttentionSignal;
  closureBlocked?: boolean;
  applications?: WorkspaceApplication[];
  onOpenFinanceContext: (p: { kind: 'route' | 'application'; id: number }) => void;
  onTaskAction: (task: WorkspaceTask) => void;
}) {
  const canExecute = signal.task != null && canOpenWorkspaceTaskModal(signal.task);
  const dueLine = priorityDueLine(signal);
  const borderColor = PRIORITY_COLOR[signal.priority];
  const presentation = buildInboxTaskRowPresentation(
    signal,
    isRouteGroupDocumentWorkflowEnabled(),
    { applications }
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
        padding: '5px 0 5px 6px',
        borderBottom: '1px solid #f5f5f5',
        borderLeft: `3px solid ${borderColor}`,
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpenFinanceContext(signal.entity)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenFinanceContext(signal.entity);
          }
        }}
        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
      >
        {presentation.mode === 'scoped' && presentation.scopedLines ? (
          presentation.scopedLines.map((line, index) => (
            <Text
              key={`${signal.id}-scoped-${index}`}
              style={{ fontSize: 12, display: 'block' }}
              ellipsis={{ tooltip: presentation.tooltip }}
            >
              {line}
            </Text>
          ))
        ) : (
          <>
            <Text style={{ fontSize: 12, display: 'block' }} ellipsis={{ tooltip: presentation.primaryLine }}>
              {presentation.primaryLine}
            </Text>
            <Text style={{ fontSize: 12, display: 'block' }} ellipsis={{ tooltip: presentation.secondaryLine }}>
              {presentation.secondaryLine}
            </Text>
          </>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2, alignItems: 'center' }}>
          <PriorityBadge priority={signal.priority} />
          {dueLine ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {dueLine}
            </Text>
          ) : null}
          {closureBlocked ? <ClosureBlockedBadge /> : null}
        </div>
      </div>
      {canExecute && signal.task ? (
        <Button
          size="small"
          type="link"
          style={{ padding: '0 4px', fontSize: 12, flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onTaskAction(signal.task!);
          }}
        >
          Выполнить
        </Button>
      ) : null}
    </div>
  );
}
